import { formatMoney } from "../../toll/money";
import { campaignAcceptsDonations, campaignEnded, campaignEarnedMedal } from '../../toll/campaignRules';
import { goal_coin_small } from '../../imgs';
import { Button } from "antd";
import { useEffect } from "react";
import { MyColor } from "../../config/myStyle";
import { donation_button_icon } from "../../imgs";
import { campaignStore, campaignThemeStore, donationStore, userStore } from "../../stores";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import PageBox from "../../layout/PageBox";
import CampaignDeadline from "../../components/CampaignDeadline";
import { useTranslation } from "react-i18next";
import { getCampaignThemeImages } from "../../toll/campaignThemeVisual";

const CampaignDetail = observer(() => {
  const { t } = useTranslation();
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const campaign = campaignStore.campaignMap.get(campaignId ?? '');
  const themeLocale = userStore.language ?? "zh-HK";
  const themeName = campaignThemeStore.campaignThemeDataList?.items?.find(item => item.id === campaign?.campaignThemeId)?.nameObj?.[themeLocale]
    ?? campaignThemeStore.campaignThemeDataList?.items?.find(item => item.id === campaign?.campaignThemeId)?.name;
  const themeImages = getCampaignThemeImages(campaign, themeName);
  const ratioAmount = ((campaign?.raisedAmount ?? 0) / (campaign?.goalAmount ?? 0) * 100).toFixed(0);
  const campaignStatus = campaignStore.campaignStatusMap.get(campaignId ?? '');
  const donationList = donationStore.donationList;

  const isAmountHidden = (item: { isAmountHidden?: boolean; remark?: string | null }) => {
    // 新接口字段优先决定金额是否隐藏，remark 只保留给历史数据兜底使用。
    if (typeof item.isAmountHidden === "boolean") {
      return item.isAmountHidden;
    }
    if (!item.remark) return false;
    try {
      return JSON.parse(item.remark).amountVisibility === "hidden";
    } catch {
      return false;
    }
  };

  const getSupporterDisplayName = (item: typeof donationList[number]) => {
    if (item.isAnonymous) {
      return t('campaign_detail.anonymous');
    }
    // 支持者姓名按捐款接口 donorName 展示，不再拼 firstName 与 lastName。
    return item.donorName?.trim() || t('campaign_detail.anonymous');
  };

  useEffect(() => {
    campaignStore.getCampaign(campaignId ?? '');
    donationStore.getDonationList({ campaignId, limit: 1000 });
  }, [campaignId]);

  return (
    <PageBox spinning={campaignStatus?.loading || donationStore.donationListStatus.loading}>
      <div className="w-full" style={{ overflow: "hidden", marginTop: 10 }}>
        {/* 图片区 */}
        <img
          src={themeImages.cover}
          style={{
            width: "100%",
            aspectRatio: "16/9",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          alt=""
        />

        {/* 内容区 */}
        <div className="flex-col px-[20px] pt-[20px]">
          {/* 标题 */}
          <div
            style={{
              color: "#164194",
              fontWeight: 700,
              fontSize: 22,
              textWrap: 'wrap',
              lineHeight: 1.2,
            }}
          >
            {campaign?.title}
          </div>
          {/* 描述 */}
          <div
            className="text-[13px] text-[#666] mt-[6px]"
            style={{
              // 活动详情页需要完整显示 subTitle，不再限制为 3 行。
              whiteSpace: "pre-line",
              marginBottom: 12,
              lineHeight: 1.45,
            }}
          >
            {campaign?.subTitle}
          </div>
          {/* 进度条 */}
          <div className="flex items-center w-full mb-[10px]">
            <div
              style={{
                background: "#e5e5e5",
                height: 15,
                borderRadius: 15,
                flex: 1,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  background: MyColor.red,
                  height: 15,
                  width: `${ratioAmount}%`,
                  transition: "width 0.4s",
                }}
              />
            </div>
          </div>
          {/* 金额和目标 */}
          <div className="flex items-end justify-between mt-[10px] mb-[6px]">
            <div className="flex flex-col items-start">
              <div
                style={{
                  color: MyColor.red,
                  fontWeight: 600,
                  fontSize: 14,
                  fontStyle: "italic",
                  marginBottom: -2,
                }}
              >
                {t("campaign_detail.raised")}
              </div>
              <div
                style={{
                  color: MyColor.red,
                  fontWeight: 600,
                  fontSize: 23,
                }}
              >
                HK$ {formatMoney(campaign?.raisedAmount)}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <div
                style={{
                  color: "#9b9b9b",
                  fontWeight: 600,
                  fontSize: 14,
                  fontStyle: "italic",
                  marginBottom: -2,
                }}
              >
                {t("campaign_detail.goal")}
              </div>
              <div
                style={{
                  color: "#555",
                  fontWeight: 600,
                  fontSize: 23,
                }}
              >
                HK$ {formatMoney(campaign?.goalAmount)}
              </div>
            </div>
          </div>
          {/* 百分比与时间 */}
          <div
            className="flex items-center justify-between mt-[2px] mb-[10px]"
            style={{
              fontSize: 14,
              color: "#888",
              borderTop: "2px solid rgba(0,0,0,0.2)",
              paddingTop: 5,
            }}
          >
            <div>
              <span style={{ fontWeight: 600 }}>
                {ratioAmount}%
              </span>{" "}
              {t("campaign_detail.funded")}
            </div>
            <div>
              <CampaignDeadline endDate={campaign?.expectedEndDate} />
            </div>
          </div>
          {/* Donate Now 按钮 */}
          <Button
            type="primary"
            className="w-full flex mb-[18px] mt-[12px]"
            style={{
              // 停用狀態仍保持文字清晰，方便看出活動已結束。
              background: campaignAcceptsDonations(campaign) ? MyColor.red : "#e3e3e3",
              color: campaignAcceptsDonations(campaign) ? "white" : "#555",
              border: "none",
              borderRadius: 999,
              fontSize: 22,
              height: 48,
              fontWeight: 600,
              boxShadow: "0 2px 8px #eecbe0",
              justifyContent: "center",
            }}
            disabled={!campaignAcceptsDonations(campaign)}
            onClick={() => navigate(`/donation/${campaignId}/amount`)}
          >
            <img
              src={donation_button_icon}
              alt=""
              style={{ height: 36, marginRight: 8 }}
            />
            {t(campaignAcceptsDonations(campaign) ? "campaign_detail.donate_now" : campaignEnded(campaign) ? "campaign_rules.ended" : "campaign_rules.unavailable")}
          </Button>
        </div>
        <div className="px-[18px]">
          {/* 金牌中心與分隔線對齊；底色及層級遮住下方線條，保留原有金牌尺寸。 */}
          {campaignEarnedMedal(campaign) && (
            <div className="campaign-medal-divider" style={{ position: 'relative', height: 54, marginTop: 12, marginBottom: 14 }}>
              <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 1, background: '#ccc', transform: 'translateY(-50%)' }} />
              <img src={goal_coin_small} alt={t('campaign_rules.medal')}
                style={{ position: 'relative', zIndex: 1, display: 'block', width: 54, height: 54, margin: '0 auto', borderRadius: '50%', background: MyColor.bg }} />
            </div>
          )}
          {/* 感谢支持者 */}
          {donationList.length ? (
            <div
              style={{
                borderTop: campaignEarnedMedal(campaign) ? undefined : "1px solid #ccc",
                marginTop: campaignEarnedMedal(campaign) ? 0 : 12,
                paddingTop: campaignEarnedMedal(campaign) ? 0 : 14,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  color: MyColor.blue,
                  fontWeight: 700,
                  fontSize: 18,
                  marginBottom: 10,
                  textAlign: "center",
                }}
              >
                {t("campaign_detail.thank_you_supporters")}
              </div>
              <div className="flex-col space-y-[12px]">
                {donationList.map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-[22px] px-[20px] py-[18px]"
                    style={{
                      background: "#fff",
                      boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
                    }}
                  >
                    <div className="flex items-start justify-between gap-[18px]">
                      <div className="min-w-0 flex-1">
                        <div className="text-[17px] font-[700] leading-[1.15]" style={{ color: MyColor.blue }}>Name</div>
                        <div className="mt-[6px] text-[18px] font-[500] leading-[1.2]" style={{ color: MyColor.blue }}>
                          {getSupporterDisplayName(item)}
                        </div>
                      </div>
                      <div className="mt-[4px] h-[66px] w-[2px] bg-[rgba(15,68,145,0.85)]" />
                      <div className="min-w-[126px]">
                        <div className="text-[17px] font-[700] leading-[1.15]" style={{ color: MyColor.blue }}>Amount(HK$)</div>
                        <div className="mt-[6px] text-[18px] font-[500] leading-[1.2]" style={{ color: MyColor.blue }}>
                          {isAmountHidden(item) ? t("campaign_detail.hidden_amount") : formatMoney(item.amount)}
                        </div>
                      </div>
                    </div>
                    <div className="my-[16px] h-[2px] w-full bg-[rgba(15,68,145,0.85)]" />
                    <div className="text-[18px] leading-[1.45]" style={{ color: MyColor.blue }}>
                      <span className="text-[17px] font-[700]" style={{ color: MyColor.red }}>
                        {t("campaign_detail.supporting_message")}
                      </span>
                      <div className="mt-[4px]" style={{ minHeight: item.message ? undefined : 24 }}>
                        {item.message || ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : <div />}
        </div>
      </div>
    </PageBox>
  );
})

export default CampaignDetail;
